import { useState, useEffect, useRef } from 'react'
import { X, Check, Zap, Coins } from 'lucide-react'

export default function TopUpModal({ isOpen, onClose, onSuccess, userEmail }) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [processingPayment, setProcessingPayment] = useState(false)
    const paypalRef = useRef(null)
    const paypalButtonRendered = useRef(false)

    // Use VITE_PAYPAL_CLIENT_ID from environment - user should set this to their live client ID
    const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID

    const features = [
        '50,000 AI Token Credits',
        'Valid for 1 year',
        'Access to Pro features (while credits last)',
        'No monthly commitment'
    ]

    // Load PayPal script
    useEffect(() => {
        if (!isOpen) return

        setLoading(true)
        setError(null)
        paypalButtonRendered.current = false

        const loadPayPalScript = () => {
            // Check if script already exists (might be from Lifetime modal)
            const existingScript = document.getElementById('paypal-sdk-topup') || document.getElementById('paypal-sdk-lifetime')

            if (existingScript) {
                // If it's the lifetime script, we might need to reload or handle namespace collision, 
                // but for simplicity assuming same Client ID is fine.
                renderPayPalButton()
                return
            }

            const script = document.createElement('script')
            script.id = 'paypal-sdk-topup'
            script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`
            script.setAttribute('data-sdk-integration-source', 'button-factory')

            script.onload = () => {
                renderPayPalButton()
            }

            script.onerror = () => {
                setError('Failed to load PayPal. Please try again.')
                setLoading(false)
            }

            document.body.appendChild(script)
        }

        const renderPayPalButton = () => {
            if (!paypalRef.current) return
            if (!window.paypal) {
                setTimeout(renderPayPalButton, 100)
                return
            }

            paypalRef.current.innerHTML = ''
            paypalButtonRendered.current = false

            try {
                window.paypal.Buttons({
                    style: {
                        shape: 'pill',
                        color: 'blue',
                        layout: 'vertical',
                        label: 'pay'
                    },
                    createOrder: async function (data, actions) {
                        return actions.order.create({
                            purchase_units: [{
                                description: 'InkSpot - 50k Credit Top-up',
                                amount: {
                                    value: '3.00',
                                    currency_code: 'USD'
                                }
                            }]
                        })
                    },
                    onApprove: async function (data, actions) {
                        setProcessingPayment(true)
                        try {
                            const details = await actions.order.capture()
                            console.log('Payment captured:', details)

                            // Call API to credit tokens
                            const response = await fetch('/api/payments/top-up', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    orderId: data.orderID,
                                    email: userEmail || details.payer?.email_address,
                                    credits: 50000
                                })
                            })

                            if (response.ok) {
                                if (onSuccess) onSuccess()
                                onClose()
                                alert('🎉 Credits added successfully!')
                                window.location.reload()
                            } else {
                                throw new Error('Failed to process payment')
                            }
                        } catch (err) {
                            console.error('Error processing payment:', err)
                            setError('Payment received but credit allocation failed. Please contact support.')
                        } finally {
                            setProcessingPayment(false)
                        }
                    },
                    onError: function (err) {
                        console.error('PayPal error:', err)
                        setError('Payment failed. Please try again.')
                    }
                }).render(paypalRef.current)

                paypalButtonRendered.current = true
                setLoading(false)
            } catch (err) {
                console.error('Error rendering PayPal button:', err)
                setError('Failed to initialize PayPal. Please try again.')
                setLoading(false)
            }
        }

        setTimeout(loadPayPalScript, 100)

        return () => {
            paypalButtonRendered.current = false
        }
    }, [isOpen, PAYPAL_CLIENT_ID, onClose, onSuccess, userEmail])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            <div className="relative w-full max-w-lg glass-card p-8 animate-scale-in max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                        <Coins className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                        Top Up Credits
                    </h2>
                    <p className="text-white/60">
                        Pay as you go. No monthly commitments.
                    </p>
                </div>

                <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-2">
                        <span className="text-5xl font-bold text-white">$3</span>
                        <span className="text-white/50">/ 50k tokens</span>
                    </div>
                    <p className="text-sm text-blue-400 mt-2">
                        Enough for ~50 blog posts
                    </p>
                </div>

                <div className="space-y-2 mb-8">
                    {features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <Check className="w-3 h-3 text-blue-400" />
                            </div>
                            <span className="text-white/80 text-sm">{feature}</span>
                        </div>
                    ))}
                </div>

                <div className="min-h-[50px]">
                    {loading && (
                        <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-3 text-white/60">Loading payment options...</span>
                        </div>
                    )}

                    {processingPayment && (
                        <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-3 text-white/60">Processing top-up...</span>
                        </div>
                    )}

                    {error && (
                        <div className="text-center py-4">
                            <p className="text-red-400 mb-2">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="text-primary-400 hover:underline"
                            >
                                Refresh page
                            </button>
                        </div>
                    )}

                    <div ref={paypalRef} className={loading || processingPayment ? 'hidden' : ''} />
                </div>
            </div>
        </div>
    )
}
